import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Chip,
  Switch,
  OutlinedInput,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import { Add as AddIcon, KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import EditIcon from '../../../assets/MyAccountIcon/edit.svg';
import Layout from '../../../components/Layout';
import TeacherNavbar from '../../../components/TeacherNavbar/TeacherNavbar';
import { useTranslation } from '../../../i18n/LanguageContext';
import { axiosInstance } from '../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';
import './EditSpecialisation.css';
import RequestNewSubjectModal from './RequestNewSubjectModal';
import TagResponseModal from './TagResponseModal';
import ReactDOM from 'react-dom';
import LoadingScreen from '../../../components/LoadingIcon/LoadingScreen';

// TagDropdownPortal component to render dropdown outside component hierarchy
const TagDropdownPortal = ({ children, isOpen, position, width }) => {
  if (!isOpen) return null;

  const style = {
    top: `${position.top}px`,
    left: `${position.left}px`,
    width: `${width}px`,
  };

  const enhancedChildren = React.cloneElement(React.Children.only(children), {
    style: { ...children.props.style, ...style },
  });

  return ReactDOM.createPortal(enhancedChildren, document.body);
};

// Define years of experience options (string ranges)
const yearsOfExperienceOptions = ['1-3', '4-6', '7-9', '10+'];

// Levels will be set dynamically based on teacher type - UI display values
const academicLevels = ['Elementary School', 'Middle School', 'High School', 'University'];
const creativeLevels = ['Beginner', 'Intermediate', 'Professional'];

// Map between UI display values and API values
const displayToApiLevelMap = {
  'Elementary School': 'elementary',
  'Middle School': 'middle_school',
  'High School': 'high_school',
  University: 'university',
  Beginner: 'beginner',
  Intermediate: 'intermediate',
  Professional: 'professional',
};

// Map between API values and UI display values
const apiToDisplayLevelMap = {
  elementary: 'Elementary School',
  elementary_school: 'Elementary School', // Support legacy API responses
  middle_school: 'Middle School',
  high_school: 'High School',
  university: 'University',
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  professional: 'Professional',
};

// Map API level values to UI display values
const mapApiLevelToDisplay = apiLevel => {
  return apiToDisplayLevelMap[apiLevel] || apiLevel;
};

// Map API years of experience to UI display value
const mapApiYearsToDisplay = years => {
  if (!years) return '1-3';

  // If years is already a string (like "4-6"), return it directly
  if (typeof years === 'string') {
    return years;
  }

  // If years is a number, map it to the appropriate range
  if (typeof years === 'number') {
    if (years <= 3) return '1-3';
    if (years <= 6) return '4-6';
    if (years <= 9) return '7-9';
    return '10+';
  }

  return '1-3';
};

// Map API language values to UI display values
const mapApiLanguageToDisplay = (apiLanguages, availableLanguages) => {
  if (!availableLanguages || !availableLanguages.length) return apiLanguages;

  return apiLanguages.map(langCode => {
    const language = availableLanguages.find(lang => lang.code === langCode);
    return language ? language.name : langCode;
  });
};

const normalizeTeachingLanguage = languages => {
  if (!languages) return [];

  // If it's already an array of strings, return it
  if (Array.isArray(languages) && (languages.length === 0 || typeof languages[0] === 'string')) {
    return languages;
  }

  // If it's an array of objects with 'name' properties, extract the names
  if (
    Array.isArray(languages) &&
    languages.length > 0 &&
    typeof languages[0] === 'object' &&
    languages[0]?.name
  ) {
    return languages.map(lang => lang.name);
  }

  // Fallback for any other format
  return [];
};

const EditSpecialisation = () => {
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  // State for available languages from API
  const [availableLanguages, setAvailableLanguages] = useState([]);

  // State to track teacher type (academic or creative)
  const [teacherType, setTeacherType] = useState('academic');

  // Current levels based on teacher type
  const [currentLevels, setCurrentLevels] = useState(academicLevels);

  // Teacher profile data
  const [teacherProfile, setTeacherProfile] = useState({
    level: [],
    yearsOfExperience: null,
    teachingLanguage: [],
    subjects: [],
    pendingSubjects: [],
  });

  // Edit states
  const [editingExperience, setEditingExperience] = useState(false);
  const [editingSubjectId, setEditingSubjectId] = useState(null);

  // State for available tags from API
  const [availableTags, setAvailableTags] = useState([]);

  // Modals
  const [showAddSubjectModal, setShowAddSubjectModal] = useState(false);

  const [tempTagInput, setTempTagInput] = useState({});

  // State for tag suggestions
  const [tagSuggestions, setTagSuggestions] = useState([]);

  // Add state for dropdown position
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 300 });

  const [tagResponseModal, setTagResponseModal] = useState({ open: false, response: null });

  // Add a state to store the original values for experience section
  const [originalExperienceValues, setOriginalExperienceValues] = useState({
    level: [],
    yearsOfExperience: null,
    teachingLanguage: [],
  });

  // Add useEffect for click outside handler
  useEffect(() => {
    const handleClickOutside = event => {
      // If there are tag suggestions showing and the click is outside the dropdown
      if (tagSuggestions.length > 0) {
        const dropdown = document.querySelector('.tags-dropdown');
        // Check if dropdown exists and the click was outside it
        if (dropdown && !dropdown.contains(event.target)) {
          // Check if the click was on an input field
          const isInputClick = event.target.closest('.tag-input-container');
          if (!isInputClick) {
            setTagSuggestions([]);
          }
        }
      }
    };

    // Add the event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tagSuggestions]);

  // Fetch available languages
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        // First fetch available languages
        const languagesResponse = await axiosInstance.get('/auth/languages');

        setAvailableLanguages(languagesResponse.data);

        // Then fetch user profile to get selected languages
        const userProfileResponse = await axiosInstance.get('/auth/users/me');

        // Check if user has languages set
        if (userProfileResponse.data.languages && userProfileResponse.data.languages.length > 0) {
          // Extract language names from the response
          const languageNames = userProfileResponse.data.languages.map(lang => lang.name);

          // Update teaching languages in profile from user data
          setTeacherProfile(prevProfile => {
            return {
              ...prevProfile,
              teachingLanguage: languageNames,
            };
          });
        } else {
          // If no languages set, show "No language selected"
          setTeacherProfile(prevProfile => ({
            ...prevProfile,
            teachingLanguage: [],
          }));
        }
      } catch (error) {
        console.error('Error fetching languages:', error);
        // Don't set fallback data, just set empty array
        setAvailableLanguages([]);
      }
    };

    fetchLanguages();
  }, []);

  // Update levels based on teacher type
  useEffect(() => {
    if (teacherType === 'academic') {
      setCurrentLevels(academicLevels);
    } else {
      setCurrentLevels(creativeLevels);
    }
  }, [teacherType]);

  // Fetch teacher profile data, pending subjects, and disabled subjects
  useEffect(() => {
    const fetchTeacherData = async () => {
      setIsLoading(true);
      try {
        // Fetch teacher profile data
        const profileResponse = await axiosInstance.get('/teachers');

        // Set teacher type from profile response
        setTeacherType(profileResponse.data.teacher_type || 'academic');

        // Fetch pending subject requests
        const pendingSubjectsResponse = await axiosInstance.get(
          '/teachers/subjects/requests?limit=20&offset=0'
        );

        // Map levels from API to UI format based on teacher type
        const mappedLevels = profileResponse.data.level_of_teaching
          ? profileResponse.data.level_of_teaching.map(level => mapApiLevelToDisplay(level))
          : [];

        // Map years of experience from API to UI format
        const mappedYearsOfExperience = mapApiYearsToDisplay(
          profileResponse.data.years_of_experience || 0
        );

        // Map teaching languages from API to UI format
        const mappedTeachingLanguages =
          profileResponse.data.teaching_lang && availableLanguages.length
            ? mapApiLanguageToDisplay(profileResponse.data.teaching_lang, availableLanguages)
            : [];

        // Format active subjects - filter based on is_active field
        const formattedSubjects = profileResponse.data.subjects
          ? profileResponse.data.subjects
              .filter(subject => subject.is_active === true)
              .map(subject => ({
                id: subject.id,
                title: subject.title,
                name: subject.title,
                tags: subject.tags ? subject.tags.map(tag => tag.title) : [],
                enabled: true,
                isEditing: false,
              }))
          : [];

        // Format disabled subjects - filter based on is_active field
        const formattedDisabledSubjects = profileResponse.data.subjects
          ? profileResponse.data.subjects
              .filter(subject => subject.is_active === false)
              .map(subject => ({
                id: subject.id,
                title: subject.title,
                name: subject.title,
                tags: subject.tags ? subject.tags.map(tag => tag.title) : [],
                enabled: false,
                isEditing: false,
              }))
          : [];

        // Format pending subjects - handle new response structure with results array
        const formattedPendingSubjects =
          pendingSubjectsResponse.data &&
          pendingSubjectsResponse.data.results &&
          pendingSubjectsResponse.data.results.length
            ? pendingSubjectsResponse.data.results.map(request => ({
                id: request.id,
                title: request.subject.title,
                name: request.subject.title,
                tags: request.subject.tags ? request.subject.tags.map(tag => tag.title) : [],
                status: request.status || 'pending',
              }))
            : [];

        // Update teacher profile state while preserving teaching languages
        setTeacherProfile(prevProfile => ({
          ...prevProfile,
          level: mappedLevels,
          yearsOfExperience: mappedYearsOfExperience,
          subjects: [...formattedSubjects, ...formattedDisabledSubjects],
          pendingSubjects: formattedPendingSubjects,
        }));
      } catch (error) {
        console.error('Error fetching teacher data:', error);
        // toast.error('Failed to load profile data');

        // Don't set dummy data, just initialize with empty values while preserving languages
        setTeacherProfile(prevProfile => ({
          ...prevProfile,
          level: [],
          yearsOfExperience: null,
          subjects: [],
          pendingSubjects: [],
        }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeacherData();
  }, [availableLanguages]);

  // Handle opening the add subject modal
  const handleOpenAddSubjectModal = () => {
    setShowAddSubjectModal(true);
  };

  const handleExperienceChange = (field, value) => {
    setTeacherProfile(prevProfile => ({
      ...prevProfile,
      [field]: value,
    }));
  };

  const handleTagChange = (subjectId, tag, action) => {
    if (action === 'remove') {
      // Find the tag ID if it exists in availableTags
      const tagToRemove = availableTags.find(t => t.title === tag);

      if (tagToRemove && tagToRemove.id) {
        // Call the API to remove the tag
        axiosInstance
          .delete('/teachers/remove/subjects-tags', {
            data: {
              tag_ids: [tagToRemove.id],
            },
          })
          .then(() => {
            // Update UI after successful API call
            const updatedSubjects = teacherProfile.subjects.map(subject => {
              if (subject.id === subjectId) {
                return { ...subject, tags: subject.tags.filter(t => t !== tag) };
              }
              return subject;
            });

            setTeacherProfile({
              ...teacherProfile,
              subjects: updatedSubjects,
            });

            toast.success(`"${tag}" tag removed successfully`);
          })
          .catch(error => {
            console.error('Error removing tag:', error);
            toast.error('Failed to remove tag. Please try again.');
          });
      } else {
        // If tag doesn't have an ID (locally added tag), just remove from UI
        const updatedSubjects = teacherProfile.subjects.map(subject => {
          if (subject.id === subjectId) {
            return { ...subject, tags: subject.tags.filter(t => t !== tag) };
          }
          return subject;
        });

        setTeacherProfile({
          ...teacherProfile,
          subjects: updatedSubjects,
        });

        toast.success(`"${tag}" tag removed successfully`);
      }
    } else if (
      action === 'add' &&
      !teacherProfile.subjects.find(s => s.id === subjectId).tags.includes(tag)
    ) {
      // Add tag directly to the UI (for dropdown selection)
      const updatedSubjects = teacherProfile.subjects.map(subject => {
        if (subject.id === subjectId) {
          return { ...subject, tags: [...subject.tags, tag] };
        }
        return subject;
      });

      setTeacherProfile({
        ...teacherProfile,
        subjects: updatedSubjects,
      });
    }
  };

  // Helper function to check if a tag is already selected for a subject
  const isTagSelected = (subjectId, tagTitle) => {
    const subject = teacherProfile.subjects.find(s => s.id === subjectId);
    return subject && subject.tags && subject.tags.includes(tagTitle);
  };

  // Add a case-insensitive checker to avoid duplicates with different casing
  const isTagAlreadySelected = (subjectId, tagName) => {
    const subject = teacherProfile.subjects.find(s => s.id === subjectId);
    if (!subject || !tagName) return false;
    const lower = tagName.toLowerCase();
    return Array.isArray(subject.tags)
      ? subject.tags.some(t => (t || '').toLowerCase() === lower)
      : false;
  };

  // Add this new function for fetching tag suggestions
  const fetchTagSuggestions = async (subjectId, inputText) => {
    // Only fetch if the input text is at least 1 character
    if (!inputText || inputText.length < 1) {
      setTagSuggestions([]);
      return;
    }

    try {
      const response = await axiosInstance.get(`/common/${subjectId}/tags`, {
        params: { query: inputText, limit: 20, offset: 0 },
      });

      // Extract tags from the new paginated response structure
      const data = response.data;
      const tags = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];

      // Filter tags that match the input text and are not already selected
      const filteredTags = tags.filter(tag => {
        const tagName = tag.name || tag.title || '';
        if (isTagAlreadySelected(subjectId, tagName)) return false;
        return tagName.toLowerCase().includes(inputText.toLowerCase());
      });

      setTagSuggestions(filteredTags);
    } catch (error) {
      console.error('Error fetching tag suggestions:', error);
      setTagSuggestions([]);
    }
  };

  const handleTagInputChange = (subjectId, value) => {
    // Update the current tag input value in the state
    setTempTagInput(prev => ({
      ...prev,
      [subjectId]: value,
    }));

    // Fetch tag suggestions based on the input text - now from first character
    if (value.trim().length >= 1) {
      fetchTagSuggestions(subjectId, value.trim());

      // Position the dropdown relative to the input field
      setTimeout(() => {
        const inputElement = document.querySelector(`#tag-input-${subjectId}`);
        if (inputElement) {
          const rect = inputElement.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width, // Capture the width of the input field
          });
        }
      }, 0);
    } else {
      setTagSuggestions([]);
    }
  };

  const handleEditSubject = async subjectId => {
    // First fetch available tags for this subject
    try {
      const response = await axiosInstance.get(`/common/${subjectId}/tags`);

      // Support new paginated response structure
      const tags = Array.isArray(response.data?.results)
        ? response.data.results
        : Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];

      setAvailableTags(tags);
    } catch (error) {
      console.error('Error fetching tags for subject:', error);
      setAvailableTags([]);
    }

    const updatedSubjects = teacherProfile.subjects.map(subject => {
      if (subject.id === subjectId) {
        return { ...subject, isEditing: true };
      }
      return { ...subject, isEditing: false };
    });

    setTeacherProfile({
      ...teacherProfile,
      subjects: updatedSubjects,
    });
  };

  // Add a function to refresh all necessary data
  const refreshData = async () => {
    try {
      setIsLoading(true);

      // Fetch teacher profile data
      const profileResponse = await axiosInstance.get('/teachers');

      // Set teacher type from profile response
      setTeacherType(profileResponse.data.teacher_type || 'academic');

      // Fetch pending subject requests
      const pendingSubjectsResponse = await axiosInstance.get(
        '/teachers/subjects/requests?limit=20&offset=0'
      );

      // Map levels from API to UI format based on teacher type
      const mappedLevels = profileResponse.data.level_of_teaching
        ? profileResponse.data.level_of_teaching.map(level => mapApiLevelToDisplay(level))
        : [];

      // Map years of experience from API to UI format
      const mappedYearsOfExperience = mapApiYearsToDisplay(
        profileResponse.data.years_of_experience || 0
      );

      // Map teaching languages from API to UI format
      const mappedTeachingLanguages =
        profileResponse.data.teaching_lang && availableLanguages.length
          ? mapApiLanguageToDisplay(profileResponse.data.teaching_lang, availableLanguages)
          : [];

      // Format active subjects - filter based on is_active field
      const formattedSubjects = profileResponse.data.subjects
        ? profileResponse.data.subjects
            .filter(subject => subject.is_active === true)
            .map(subject => ({
              id: subject.id,
              title: subject.title,
              name: subject.title,
              tags: subject.tags ? subject.tags.map(tag => tag.title) : [],
              enabled: true,
              isEditing: false,
            }))
        : [];

      // Format disabled subjects - filter based on is_active field
      const refreshFormattedDisabledSubjects = profileResponse.data.subjects
        ? profileResponse.data.subjects
            .filter(subject => subject.is_active === false)
            .map(subject => ({
              id: subject.id,
              title: subject.title,
              name: subject.title,
              tags: subject.tags ? subject.tags.map(tag => tag.title) : [],
              enabled: false,
              isEditing: false,
            }))
        : [];

      // Format pending subjects - handle new response structure with results array
      const formattedPendingSubjects =
        pendingSubjectsResponse.data &&
        pendingSubjectsResponse.data.results &&
        pendingSubjectsResponse.data.results.length
          ? pendingSubjectsResponse.data.results.map(request => ({
              id: request.id,
              title: request.subject.title,
              name: request.subject.title,
              tags: request.subject.tags ? request.subject.tags.map(tag => tag.title) : [],
              status: 'pending',
            }))
          : [];

      // Update teacher profile state while preserving teaching languages
      setTeacherProfile(prevProfile => ({
        ...prevProfile,
        level: mappedLevels,
        yearsOfExperience: mappedYearsOfExperience,
        subjects: [...formattedSubjects, ...refreshFormattedDisabledSubjects],
        pendingSubjects: formattedPendingSubjects,
      }));
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh profile data');
    } finally {
      setIsLoading(false);
    }
  };

  // Update handleToggleSubjectStatus to use the new API
  const handleToggleSubjectStatus = async subjectId => {
    try {
      const subject = teacherProfile.subjects.find(s => s.id === subjectId);
      if (!subject) {
        toast.error('Subject not found');
        return;
      }

      // Determine the action based on current enabled status
      const action = subject.enabled ? 'disable' : 'enable';

      // Call the API to update the subject status
      await axiosInstance.patch(`/teachers/subject/enable-disable/${subjectId}`, {
        action: action,
      });

      // Optimistically update the UI
      const updatedSubjects = teacherProfile.subjects.map(s => {
        if (s.id === subjectId) {
          return {
            ...s,
            enabled: !s.enabled,
            isEditing: false,
          };
        }
        return s;
      });

      setTeacherProfile({
        ...teacherProfile,
        subjects: updatedSubjects,
      });

      // Give user feedback
      toast.success(
        action === 'enable' ? 'Subject enabled successfully' : 'Subject disabled successfully'
      );

      // Refresh data to ensure everything is up to date
      refreshData();
    } catch (error) {
      console.error('Error toggling subject status:', error);
      toast.error('Failed to update subject status. Please try again.');
    }
  };

  const saveExperienceChanges = async () => {
    try {
      setIsLoading(true);

      // Prepare experience data for the API using the current teacherProfile state
      const experienceData = {
        years_of_experience: teacherProfile.yearsOfExperience, // Send the string directly
        level_of_teaching: teacherProfile.level.map(level => {
          const normalizedLevel = level.trim();

          // First try exact case match
          if (displayToApiLevelMap[normalizedLevel]) {
            return displayToApiLevelMap[normalizedLevel];
          }

          // Try case-insensitive match
          const caseInsensitiveMatch = Object.keys(displayToApiLevelMap).find(
            key => key.toLowerCase() === normalizedLevel.toLowerCase()
          );

          if (caseInsensitiveMatch) {
            return displayToApiLevelMap[caseInsensitiveMatch];
          }

          // If no match found, convert to snake_case as fallback
          return normalizedLevel.toLowerCase().replace(/\s+/g, '_');
        }),
      };

      // First update experience (levels and years)
      await axiosInstance.patch('/teachers', experienceData);

      // Then update languages in user profile
      try {
        // First get current user profile to get existing values
        const userResponse = await axiosInstance.get('/auth/users/me');
        const userData = userResponse.data;

        // Convert language names to language IDs
        const languageIds = [];
        if (teacherProfile.teachingLanguage && teacherProfile.teachingLanguage.length > 0) {
          teacherProfile.teachingLanguage.forEach(languageName => {
            const foundLanguage = availableLanguages.find(
              lang =>
                lang.name === languageName ||
                lang.name?.toLowerCase() === languageName?.toLowerCase()
            );
            if (foundLanguage && foundLanguage.id) {
              languageIds.push(foundLanguage.id);
            } else {
              console.warn(`Language "${languageName}" not found in available languages`);
            }
          });
        }

        console.log('Updating languages:', {
          selectedLanguageNames: teacherProfile.teachingLanguage,
          languageIds: languageIds,
        });

        // Then update with existing values plus new languages (sending language IDs)
        const userUpdatePayload = {
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          phone_no: userData.phone_no || '',
          profile_image: userData.profile_image || '',
          dob: userData.dob || '',
          gender: userData.gender || '',
          country_id: userData.country?.id || null,
          city_id: userData.city?.id || null,
          language_ids: languageIds, // Sending language IDs, not names
        };

        console.log('Sending user update payload:', userUpdatePayload);
        await axiosInstance.patch('/auth/users/me', userUpdatePayload);
      } catch (userError) {
        console.error('Error updating user languages:', userError);
        toast.error('Failed to update teaching languages. Other changes were saved.');
      }

      toast.success('Experience updated successfully');
      setEditingExperience(false);
    } catch (error) {
      console.error('Error updating experience:', error);
      toast.error(
        error.response?.data?.message || 'Failed to update experience. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const saveSubjectChanges = async subjectId => {
    try {
      // Get tags that need to be saved
      const subject = teacherProfile.subjects.find(s => s.id === subjectId);

      if (subject) {
        // Get tag IDs from available tags
        const tagIds = [];
        subject.tags.forEach(tagTitle => {
          const foundTag = availableTags.find(tag => tag.title === tagTitle);
          if (foundTag) {
            tagIds.push(foundTag.id);
          }
        });

        // Update teacher profile with selected tags
        await axiosInstance.patch('/teachers', {
          tags: tagIds,
          level_of_teaching: teacherProfile.level.map(level => {
            // Convert display level to API level
            return displayToApiLevelMap[level] || level.toLowerCase().replace(/\s+/g, '_');
          }),
          years_of_experience: teacherProfile.yearsOfExperience, // Send the string directly
        });
      }

      const updatedSubjects = teacherProfile.subjects.map(subject => {
        if (subject.id === subjectId) {
          return { ...subject, isEditing: false };
        }
        return subject;
      });

      setTeacherProfile({
        ...teacherProfile,
        subjects: updatedSubjects,
      });

      toast.success('Subject updated successfully');
    } catch (error) {
      console.error('Error saving subject changes:', error);
      toast.error('Failed to save subject changes. Please try again.');
    }
  };

  // Update handleAddTag function
  const handleAddTag = async (subjectId, tagValue, tagId = null) => {
    if (!tagValue) {
      toast.error('Please enter a tag to add');
      return;
    }

    try {
      // If we have a tagId, it means this is a tag from the dropdown (existing tag)
      if (tagId) {
        // For existing tags, directly add to the subject's tags
        const subject = teacherProfile.subjects.find(s => s.id === subjectId);

        if (subject && !subject.tags.includes(tagValue)) {
          // Update the UI
          const updatedSubjects = teacherProfile.subjects.map(s => {
            if (s.id === subjectId) {
              return { ...s, tags: [...s.tags, tagValue] };
            }
            return s;
          });

          setTeacherProfile({
            ...teacherProfile,
            subjects: updatedSubjects,
          });

          // Make API call to update the tags for this subject
          try {
            // Get current tag IDs that need to be preserved
            const existingTagIds = [];
            subject.tags.forEach(tagTitle => {
              const foundTag = availableTags.find(tag => tag.title === tagTitle);
              if (foundTag) {
                existingTagIds.push(foundTag.id);
              }
            });

            // Add the new tag ID
            const tagIds = [...existingTagIds, tagId];

            // Update teacher profile with selected tags
            await axiosInstance.patch('/teachers', {
              tags: tagIds,
              level_of_teaching: teacherProfile.level.map(level => {
                // Convert display level to API level
                return displayToApiLevelMap[level] || level.toLowerCase().replace(/\s+/g, '_');
              }),
              years_of_experience: teacherProfile.yearsOfExperience, // Send the string directly
            });

            toast.success(`"${tagValue}" tag added successfully`);
          } catch (error) {
            console.error('Error adding tag:', error);
            toast.error('Failed to add tag. Please try again.');
          }
        }
      } else {
        // For new tags, make a request to the tag request API
        const response = await axiosInstance.post('/teachers/tags/request', {
          subject_id: subjectId,
          tag_title: tagValue,
        });

        // Show the response modal
        setTagResponseModal({
          open: true,
          response: response.data,
        });

        // If the response was successful, add the tag to the UI
        if (response.data.status === 'success') {
          const updatedSubjects = teacherProfile.subjects.map(s => {
            if (s.id === subjectId) {
              return { ...s, tags: [...s.tags, response.data.tag.title] };
            }
            return s;
          });

          setTeacherProfile({
            ...teacherProfile,
            subjects: updatedSubjects,
          });
        }
      }

      // Clear the input but don't clear suggestions
      // This allows the user to continue adding tags
      setTempTagInput(prev => ({
        ...prev,
        [subjectId]: '',
      }));

      // Refocus the input field
      setTimeout(() => {
        const inputElement = document.querySelector(`#tag-input-${subjectId}`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    } catch (error) {
      console.error('Error processing tag:', error);

      // Show error modal
      setTagResponseModal({
        open: true,
        response: error.response?.data || {
          status: 'error',
          message: 'Failed to process tag. Please try again.',
        },
      });
    }
  };

  const handleCancelSubjectEdit = subjectId => {
    const updatedSubjects = teacherProfile.subjects.map(subject => {
      if (subject.id === subjectId) {
        return { ...subject, isEditing: false };
      }
      return subject;
    });

    setTeacherProfile({
      ...teacherProfile,
      subjects: updatedSubjects,
    });
  };

  const handleSaveAll = async () => {
    try {
      toast.success('Profile updated successfully');
      navigate('/instructor/account');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile changes');
    }
  };

  // Update the function that opens the editing experience mode
  const handleStartEditingExperience = () => {
    // Save the current values before editing
    setOriginalExperienceValues({
      level: [...teacherProfile.level],
      yearsOfExperience: teacherProfile.yearsOfExperience,
      teachingLanguage: [...teacherProfile.teachingLanguage],
    });

    setEditingExperience(true);
  };

  // Update the cancel function to restore original values
  const cancelEditingExperience = () => {
    // Restore the original values from before editing started
    setTeacherProfile(prevProfile => ({
      ...prevProfile,
      level: originalExperienceValues.level,
      yearsOfExperience: originalExperienceValues.yearsOfExperience,
      teachingLanguage: originalExperienceValues.teachingLanguage,
    }));

    setEditingExperience(false);
  };

  // Helper function to display array values as comma-separated string
  const displayArrayAsString = arr => {
    if (!arr) return 'No language selected';
    if (Array.isArray(arr) && arr.length === 0) return 'No language selected';

    // If it's an array of objects with 'name' properties, extract the names
    if (Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'object' && arr[0]?.name) {
      return arr.map(item => item.name).join(', ');
    }

    return Array.isArray(arr) ? arr.join(', ') : arr;
  };

  // Function that filters available tags based on input
  const getFilteredTags = (subjectId, input) => {
    if (!input) return [];

    return availableTags.filter(tag => tag.title.toLowerCase().includes(input.toLowerCase()));
  };

  // Add these styles to the component's styles section
  const styles = {
    tagSuggestionsContainer: {
      position: 'absolute',
      width: '100%',
      maxHeight: '150px',
      overflowY: 'auto',
      backgroundColor: '#fff',
      border: '1px solid #ddd',
      borderRadius: '4px',
      zIndex: 1000,
      marginTop: '2px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    },
    suggestionItem: {
      padding: '8px 12px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    },
  };

  const handleSelectTagSuggestion = (subjectId, tagName, tagId) => {
    // For tags from dropdown, we have both the name and ID
    if (tagId) {
      // Add the selected tag with its ID (existing tag)
      handleAddTag(subjectId, tagName, tagId);
    } else {
      // Fallback for tags without ID (should not happen for dropdown items)
      handleAddTag(subjectId, tagName);
    }

    // Don't clear the input and suggestions
    // Instead, keep the dropdown open and focus back on the input
    setTimeout(() => {
      const inputElement = document.querySelector(`#tag-input-${subjectId}`);
      if (inputElement) {
        inputElement.focus();
      }
    }, 100);
  };

  // Update the handleTagInputKeyPress function to pass the proper parameters
  const handleTagInputKeyPress = (e, subjectId) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      const inputValue = tempTagInput[subjectId];
      if (!inputValue || !inputValue.trim()) return;

      // Prevent duplicate selection or unnecessary requests if already selected
      if (isTagAlreadySelected(subjectId, inputValue)) {
        return;
      }

      // Check if the input matches any tag in suggestions
      const matchingTag = tagSuggestions.find(
        tag => (tag.name || tag.title || '').toLowerCase() === inputValue.toLowerCase()
      );

      if (matchingTag) {
        // If it's a match, use the tag ID
        handleAddTag(subjectId, matchingTag.name || matchingTag.title, matchingTag.id);
      } else {
        // Otherwise, send for verification
        handleAddTag(subjectId, inputValue);
      }

      // Don't clear the input - keep the dropdown open
      // Just focus back on the input after adding the tag
      setTimeout(() => {
        const inputElement = document.querySelector(`#tag-input-${subjectId}`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    }
  };

  // Update the onClose handler for the modal to refresh data
  const handleCloseAddSubjectModal = () => {
    setShowAddSubjectModal(false);
    refreshData();
  };

  // Add modal close handler
  const handleCloseTagResponseModal = () => {
    setTagResponseModal({ open: false, response: null });
  };

  if (isLoading) {
    return (
      <Layout title="Klass Online | My Account">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <TeacherNavbar />
            <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px] flex justify-center items-center">
              <LoadingScreen />
            </Box>
          </Box>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Klass Online | Edit Specialisation">
      <Box className="page-container">
        <Box className="navbar-container">
          <TeacherNavbar />
        </Box>

        <Box className="content-container">
          {/* Breadcrumb */}
          <Box className="breadcrumb-container">
            <Typography
              variant="body2"
              className="breadcrumb-item"
              onClick={() => navigate('/instructor/account')}
            >
              My Account
            </Typography>
            <Typography variant="body2" className="breadcrumb-separator">
              &gt;
            </Typography>
            <Typography
              variant="body2"
              className="breadcrumb-item"
              onClick={() => navigate('/instructor/account')}
            >
              Edit Specialisation
            </Typography>
            <Typography variant="body2" className="breadcrumb-separator">
              &gt;
            </Typography>
            <Typography variant="body2" className="breadcrumb-item-active">
              Details
            </Typography>
          </Box>

          <>
            {/* Experience Section - Header outside */}
            <Box className="section-outer">
              <Box className="section-header-outer">
                <Typography variant="h6">Experience</Typography>

                <IconButton
                  size="small"
                  onClick={handleStartEditingExperience}
                  className="edit-button"
                >
                  <img src={EditIcon} alt="Edit" className="w-4 h-4" />
                </IconButton>
              </Box>

              <Box className="section-card">
                <Box className="section-content">
                  {editingExperience ? (
                    <>
                      <Box className="experience-full-width-container">
                        <Box className="experience-edit-field">
                          <Typography variant="body2" className="field-label">
                            Levels
                          </Typography>
                          <FormControl fullWidth variant="outlined">
                            <Select
                              multiple
                              value={teacherProfile.level}
                              onChange={e => handleExperienceChange('level', e.target.value)}
                              className="experience-select"
                              IconComponent={KeyboardArrowDownIcon}
                              input={<OutlinedInput />}
                              renderValue={selected => {
                                if (selected.length === 0) {
                                  return '';
                                }
                                const displayText = selected.join(', ');
                                return (
                                  <Typography className="select-display-text" noWrap>
                                    {displayText}
                                  </Typography>
                                );
                              }}
                              MenuProps={{
                                anchorOrigin: {
                                  vertical: 'bottom',
                                  horizontal: 'left',
                                },
                                transformOrigin: {
                                  vertical: 'top',
                                  horizontal: 'left',
                                },
                                PaperProps: {
                                  style: {
                                    borderRadius: '42px',
                                    maxHeight: 250,
                                    width: '300px', // Match parent width
                                    marginTop: '8px',
                                  },
                                  className: 'experience-dropdown-menu',
                                },
                              }}
                            >
                              {currentLevels.map(level => (
                                <MenuItem
                                  key={level}
                                  value={level}
                                  className="experience-menu-item"
                                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                                >
                                  {level}
                                  <div
                                    className={`experience-checkbox-indicator ${teacherProfile.level.indexOf(level) > -1 ? 'selected' : ''}`}
                                  />
                                  <Checkbox checked={teacherProfile.level.indexOf(level) > -1} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>

                        <Box className="experience-edit-field">
                          <Typography variant="body2" className="field-label">
                            Years of Experience
                          </Typography>
                          <FormControl fullWidth variant="outlined">
                            <Select
                              value={teacherProfile.yearsOfExperience}
                              onChange={e =>
                                handleExperienceChange('yearsOfExperience', e.target.value)
                              }
                              className="experience-select year-select"
                              IconComponent={() => null}
                              renderValue={selected => (
                                <Typography className="select-display-text" noWrap>
                                  {selected}
                                </Typography>
                              )}
                              MenuProps={{
                                anchorOrigin: {
                                  vertical: 'bottom',
                                  horizontal: 'left',
                                },
                                transformOrigin: {
                                  vertical: 'top',
                                  horizontal: 'left',
                                },
                                PaperProps: {
                                  style: {
                                    borderRadius: '42px',
                                    maxHeight: 250,
                                    width: '300px', // Match parent width
                                    marginTop: '8px',
                                  },
                                  className: 'experience-dropdown-menu',
                                },
                              }}
                            >
                              {yearsOfExperienceOptions.map(year => (
                                <MenuItem
                                  key={year}
                                  value={year}
                                  className="experience-menu-item"
                                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                                >
                                  {year}
                                  <div
                                    className={`experience-checkbox-indicator ${teacherProfile.yearsOfExperience === year ? 'selected' : ''}`}
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>

                        <Box className="experience-edit-field">
                          <Typography variant="body2" className="field-label">
                            Teaching Language
                          </Typography>
                          <FormControl fullWidth variant="outlined">
                            <Select
                              multiple
                              value={normalizeTeachingLanguage(teacherProfile.teachingLanguage)}
                              onChange={e =>
                                handleExperienceChange('teachingLanguage', e.target.value)
                              }
                              className="experience-select"
                              IconComponent={KeyboardArrowDownIcon}
                              input={<OutlinedInput />}
                              renderValue={selected => {
                                if (selected.length === 0) {
                                  return '';
                                }
                                const displayText = selected.join(', ');
                                return (
                                  <Typography className="select-display-text" noWrap>
                                    {displayText}
                                  </Typography>
                                );
                              }}
                              MenuProps={{
                                anchorOrigin: {
                                  vertical: 'bottom',
                                  horizontal: 'left',
                                },
                                transformOrigin: {
                                  vertical: 'top',
                                  horizontal: 'left',
                                },
                                PaperProps: {
                                  style: {
                                    borderRadius: '42px',
                                    maxHeight: 250,
                                    width: '300px', // Match parent width
                                    marginTop: '8px',
                                  },
                                  className: 'experience-dropdown-menu',
                                },
                              }}
                            >
                              {availableLanguages.map(language => (
                                <MenuItem
                                  key={language.code}
                                  value={language.name}
                                  className="experience-menu-item"
                                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                                >
                                  {language.name}
                                  <div
                                    className={`experience-checkbox-indicator ${teacherProfile.teachingLanguage.indexOf(language.name) > -1 ? 'selected' : ''}`}
                                  />
                                  <Checkbox
                                    checked={
                                      teacherProfile.teachingLanguage.indexOf(language.name) > -1
                                    }
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>

                      <Box className="experience-button-container">
                        <Button
                          variant="outlined"
                          onClick={cancelEditingExperience}
                          className="cancel-button-exp"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={saveExperienceChanges}
                          className="save-button-exp"
                        >
                          Save
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <Box className="section-grid">
                      <Box>
                        <Typography variant="body2" className="field-label">
                          Levels
                        </Typography>
                        <Typography>{displayArrayAsString(teacherProfile.level)}</Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" className="field-label">
                          Years of Experience
                        </Typography>
                        <Typography>{teacherProfile.yearsOfExperience}</Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" className="field-label">
                          Teaching Language
                        </Typography>
                        <Typography>
                          {displayArrayAsString(teacherProfile.teachingLanguage)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Subjects Section - Header outside */}
            <Box className="section-outer">
              <Box className="section-header-outer">
                <Typography variant="h6">Subjects</Typography>

                <Button
                  variant="text"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddSubjectModal}
                  className="add-subjects-button"
                >
                  Add Subjects
                </Button>
              </Box>

              {/* Active Subjects */}
              {teacherProfile.subjects
                .filter(subject => subject.enabled)
                .map((subject, index) => (
                  <Box key={subject.id} className="section-card subject-section-card">
                    <Box className="subject-item">
                      <Box className="subject-header">
                        <Typography variant="subtitle1">Subject </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {subject.isEditing ? (
                            <Switch
                              checked={subject.enabled}
                              onChange={() => handleToggleSubjectStatus(subject.id)}
                              className="toggle-switch"
                            />
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => handleEditSubject(subject.id)}
                              className="edit-button"
                            >
                              <img src={EditIcon} alt="Edit" className="w-4 h-4" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>

                      <Box className="subject-content">
                        <Typography variant="body2" className="field-label">
                          Title
                        </Typography>
                        <Typography>{subject.name}</Typography>
                      </Box>

                      {/* Only show tags section if subject has tags or is in edit mode */}
                      {(subject.tags?.length > 0 || subject.isEditing) && (
                        <Box className="subject-tags">
                          <Typography variant="body2" className="field-label">
                            Tags
                          </Typography>

                          {subject.isEditing ? (
                            <>
                              <FormControl fullWidth sx={{ position: 'relative' }}>
                                <div className="tag-input-container">
                                  <TextField
                                    id={`tag-input-${subject.id}`}
                                    size="small"
                                    placeholder="Type here..."
                                    value={tempTagInput[subject.id] || ''}
                                    onChange={e => handleTagInputChange(subject.id, e.target.value)}
                                    onKeyPress={e => handleTagInputKeyPress(e, subject.id)}
                                    className="tag-input"
                                  />
                                </div>

                                {/* Tag suggestions dropdown using portal with position */}
                                <TagDropdownPortal
                                  isOpen={tagSuggestions.length > 0 || tempTagInput[subject.id]}
                                  position={dropdownPosition}
                                  width={dropdownPosition.width}
                                >
                                  <div className="tags-dropdown">
                                    <div className="tags-search-results">
                                      {tagSuggestions.length > 0 ? (
                                        tagSuggestions.map((tag, index) => (
                                          <div
                                            key={`${tag.id || index}-${index}`}
                                            className="tag-dropdown-item"
                                            onClick={() =>
                                              handleSelectTagSuggestion(
                                                subject.id,
                                                tag.name || tag.title,
                                                tag.id
                                              )
                                            }
                                          >
                                            <span>{tag.name || tag.title}</span>
                                            <div
                                              className={`tag-selection-indicator ${
                                                isTagSelected(subject.id, tag.name || tag.title)
                                                  ? 'selected'
                                                  : ''
                                              }`}
                                            />
                                          </div>
                                        ))
                                      ) : tempTagInput[subject.id] ? (
                                        <div className="no-tags-found">No matching tags found</div>
                                      ) : null}
                                    </div>

                                    {/* Show "Add Tag" button when there's input but no matching tags */}
                                    {tempTagInput[subject.id] && (
                                      <div className="tags-dropdown-footer">
                                        <Button
                                          className="add-tags-dropdown-button"
                                          onClick={() => {
                                            // Call handleAddTag but don't close the dropdown
                                            if (
                                              tempTagInput[subject.id] &&
                                              tempTagInput[subject.id].trim()
                                            ) {
                                              // Avoid adding if already selected (case-insensitive)
                                              if (
                                                isTagAlreadySelected(
                                                  subject.id,
                                                  tempTagInput[subject.id]
                                                )
                                              ) {
                                                return;
                                              }
                                              handleAddTag(subject.id, tempTagInput[subject.id]);
                                              // Focus back on the input field
                                              setTimeout(() => {
                                                const inputElement = document.querySelector(
                                                  `#tag-input-${subject.id}`
                                                );
                                                if (inputElement) {
                                                  inputElement.focus();
                                                }
                                              }, 100);
                                            }
                                          }}
                                        >
                                          Add Tag
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </TagDropdownPortal>
                              </FormControl>

                              <Box className="tag-container">
                                {subject.tags.map((tag, i) => (
                                  <Chip
                                    key={i}
                                    label={tag}
                                    onDelete={() => handleTagChange(subject.id, tag, 'remove')}
                                    className="tag-chip"
                                  />
                                ))}
                              </Box>

                              <Box className="section-footer">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleCancelSubjectEdit(subject.id)}
                                  className="cancel-button-exp"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => saveSubjectChanges(subject.id)}
                                  className="save-button-exp"
                                >
                                  Save
                                </Button>
                              </Box>
                            </>
                          ) : (
                            <Box className="tag-container">
                              {subject.tags.map((tag, i) => (
                                <Chip key={i} label={tag} className="tag-chip" />
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
            </Box>

            {/* Pending Subjects Section */}
            {teacherProfile.pendingSubjects && teacherProfile.pendingSubjects.length > 0 && (
              <Box className="section-outer">
                <Box className="section-header-outer">
                  <Typography variant="h6">Pending Subject Request</Typography>
                </Box>

                {/* Pending Subjects list */}
                {teacherProfile.pendingSubjects.map(subject => (
                  <Box key={subject.id} className="section-card subject-section-card">
                    <Box className="subject-item">
                      <Box className="subject-header">
                        <Typography variant="subtitle1">Subject </Typography>
                      </Box>

                      <Box className="subject-content">
                        <Typography variant="body2" className="field-label">
                          Title
                        </Typography>
                        <Typography>{subject.name}</Typography>
                      </Box>

                      {/* Tags section - always show */}
                      <Box className="subject-tags">
                        <Typography variant="body2" className="field-label">
                          Tags
                        </Typography>

                        {subject.tags && subject.tags.length > 0 ? (
                          <Box className="tag-container">
                            {subject.tags.map((tag, i) => (
                              <Chip key={i} label={tag} className="tag-chip-pending" />
                            ))}
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ color: '#949da6', fontStyle: 'italic' }}
                          >
                            No tags selected
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Disabled Subjects Section */}
            {teacherProfile.subjects.some(subject => !subject.enabled) && (
              <Box className="section-outer">
                <Box className="section-header-outer">
                  <Typography variant="h6">Disabled Subjects</Typography>
                </Box>

                {/* Disabled Subjects list */}
                {teacherProfile.subjects
                  .filter(subject => !subject.enabled)
                  .map((subject, index) => (
                    <Box key={subject.id} className="section-card subject-section-card">
                      <Box className="subject-item">
                        <Box className="subject-header">
                          <Typography variant="subtitle1">Subject </Typography>
                          <Switch
                            checked={subject.enabled}
                            onChange={() => handleToggleSubjectStatus(subject.id)}
                            className="toggle-switch"
                          />
                        </Box>

                        <Box className="subject-content">
                          <Typography variant="body2" className="field-label">
                            Title
                          </Typography>
                          <Typography sx={{ color: 'text.secondary' }}>{subject.name}</Typography>
                        </Box>

                        {/* Only show tags section if subject has tags */}
                        {subject.tags?.length > 0 && (
                          <Box className="subject-tags">
                            <Typography variant="body2" className="field-label">
                              Tags
                            </Typography>

                            <Box className="tag-container">
                              {subject.tags.map((tag, i) => (
                                <Chip key={i} label={tag} className="tag-chip-disabled" />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
              </Box>
            )}

            {/* Add Subject Modal */}
            {showAddSubjectModal && (
              <RequestNewSubjectModal
                open={showAddSubjectModal}
                onClose={handleCloseAddSubjectModal}
                onSuccess={refreshData}
              />
            )}
          </>
        </Box>
      </Box>

      {/* Add the TagResponseModal */}
      <TagResponseModal
        open={tagResponseModal.open}
        onClose={handleCloseTagResponseModal}
        response={tagResponseModal.response}
      />
    </Layout>
  );
};

export default EditSpecialisation;
